import { Action, createSelector, Selector, State, StateContext, Store } from "@ngxs/store";
import { PageModel, PageStateModel } from "./report-page.state-models";
import { Injectable } from "@angular/core";
import { AddComponentOptions, AddReportComponent, ApplyReportPageOrientation, EditPageOptions, InitPageState, RemoveComponentFromPage, SetPageOptions } from "./report-page.state-actions";
import { DetailRequest, Status, translateCommonErrorStatus } from "@core/data";
import { ReportPageService } from "@core/data/api/reports/report-page.service";
import { switchMap, tap } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import { ReportComponentService } from "@core/data/api/reports/report-component.service";
import { BaseReportComponent, CHART_OPTION_DEFAULT, clampComponentGeometry, MeasureChartComponentData, IMAGE_OPTION_DEFAULT, ImageComponentData, NewComponentOption, ReportComponentType, resolveGridDensity, scaleGridOptions, TEXT_OPTION_DEFAULT, TextComponentData, COMPONENT_CONFIG_DEFAULT, ReportPage, PageOptionConfig, PAGE_OPTION_DEFAULT, ReportPageOption, CultevaGridsterConfig, PORTRAIT_PAGE_OPTIONS, LANDSCAPE_PAGE_OPTIONS, INDEX_OPTION_DEFAULT } from "@core/data/types/advanced-report";
import { ReportComponentOptionService } from "@core/data/api/reports/report-component-option.service";
import { AddReportPageOptions } from "../../report-view/report-state/advanced-report.state-actions";
import { REPORT_STATE_TOKEN } from "../../report-view/report-state/advanced-report.state-models";
import { ReportPageOptionService } from "@core/data/api/reports/report-page-option.service";
import { TABLE_OPTION_DEFAULT, TableComponentData } from "@core/data/types/Reports/report-tables";
import { ComponentState } from "../../report-components/report-component.state";
import { ComponentModel } from "../../report-components/report-component.state-models";
import { UpdateReportComponent } from "../../report-components/report-component.state-actions";

const DEFAULTS: PageStateModel = {
    reportKey: null,
    shareKey: null,
    entities: {},
}

const PAGE_DEFAULTS: PageModel = {
    key: null,
    reportKey: null,

    componentKeys: [],

    page: null,

    status: Status.UNINITIALIZED,
}

@State<PageStateModel>({
    name: 'page_state',
    defaults: DEFAULTS,
})
@Injectable()
export class PageState {

    @Selector()
    static getEntities(state: PageStateModel) {
        return state.entities;
    }

    static getPage(key: string) {
        return createSelector(
            [PageState.getEntities],
            (entities) => entities[key]
        );
    }

    constructor(
        private _store: Store,
        private _pageService: ReportPageService,
        private _pageOptionService: ReportPageOptionService,
        private _componentService: ReportComponentService,
        private _componentOptionService: ReportComponentOptionService,
    ) {}

    @Action(InitPageState)
    initPageState(ctx: StateContext<PageStateModel>, action: InitPageState) {
        let state = ctx.getState();

        const detailRequest: DetailRequest = {
            related: [
                'options',
                'components'
            ],
        };

        ctx.patchState({
            reportKey: action.reportKey,
            shareKey: action.shareKey,
            entities: {
                ...state.entities,
                [action.pageKey]: {
                    ...PAGE_DEFAULTS,
                    key: action.pageKey,
                    status: Status.LOADING,
                    reportKey: action.reportKey,
                }
            }
        });

        return this._pageService.get(action.pageKey, action.shareKey, detailRequest)
            .pipe(
                tap(
                    result => {
                        let freshState = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...freshState.entities,
                                [action.pageKey]: {
                                    key: action.pageKey,
                                    reportKey: result.reportKey || null,
                                    componentKeys: result.components.map(data => data.key) || [],
                                    status: Status.OK,
                                    page: result,
                                }
                            }
                        });

                        //? THIS TECHNICALLY SHOULDN'T BE HIT IN THE FINAL RELEASE VERSION
                        if (!result.options) {
                            let pageKeys: string[] = [];

                            pageKeys = Object.keys(freshState.entities).map(key => key);

                            ctx.dispatch(new AddReportPageOptions(action.pageKey, pageKeys));
                        }
                    },
                    error => {
                        let freshState = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...freshState.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    page: null,
                                    key: action.pageKey,
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(EditPageOptions)
    editPageOptions(ctx: StateContext<PageStateModel>, action: EditPageOptions) {
        if (!this.editModeCheck()) return;

        let state = ctx.getState();

        const page: ReportPage = state.entities[action.pageKey]?.page;

        // page.options can be momentarily unloaded when this fires from a batched orientation
        // change (see ApplyReportPageOrientation, which dispatches after an async clamp write).
        // Fall back to the caller's own snapshot so the edit still persists — only bail if
        // neither the live state nor the caller has anything to base the config on.
        const existingOptions: ReportPageOption = page?.options ?? action.pageOptionsSnapshot;

        if (!existingOptions) {
            console.warn('[PageState] EditPageOptions skipped — no known page options', action.pageKey);
            return;
        }

        const config: PageOptionConfig = {
            ...PAGE_OPTION_DEFAULT,
            ...existingOptions.config,
            ...action.config
        };

        const options: ReportPageOption = {
            ...existingOptions,
            config: config,
        };

        ctx.patchState({
            entities: {
                ...state.entities,
                [action.pageKey]: {
                    ...state.entities[action.pageKey],
                    // Only reflect optimistically if the page is actually loaded locally — an
                    // incomplete ReportPage rebuilt from just the options snapshot would drop
                    // other page fields. If not loaded, the persisted write below still lands;
                    // whichever load picks the page back up will read the correct options.
                    ...(page ? {
                        page: {
                            ...page,
                            options: {
                                ...options
                            }
                        },
                    } : {}),
                    status: Status.LOADING,
                }
            }
        });

        return this._pageOptionService.update(action.key, options)
            .pipe(
                tap(
                    result => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    status: Status.OK,
                                }
                            }
                        });

                        // The grid may have just shrunk (e.g. an orientation flip swaps the long/short
                        // edge) — clamp any component that no longer fits instead of letting gridster's
                        // own bounds check silently delete it as "not placed".
                        const { maxCols, maxRows } = config.gridOptions;
                        const reflowActions = this.reflowComponentsForBounds(action.pageKey, maxCols, maxRows);
                        if (reflowActions.length) ctx.dispatch(reflowActions);
                    },
                    error => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    // In-memory only — no network call, no re-fetch. Used to fold options created elsewhere (e.g.
    // AddReportPageOptions' reactive fallback for a page that had none yet) into this page's entity,
    // without risking a race back into whatever created them in the first place.
    @Action(SetPageOptions)
    setPageOptions(ctx: StateContext<PageStateModel>, action: SetPageOptions) {
        const state = ctx.getState();

        const model = state.entities[action.pageKey];
        if (!model?.page) return;

        ctx.patchState({
            entities: {
                ...state.entities,
                [action.pageKey]: {
                    ...model,
                    page: {
                        ...model.page,
                        options: action.options,
                    },
                }
            }
        });
    }

    // Shrinks/repositions any of the page's components that no longer fit within maxCols/maxRows.
    // Never deletes — clamps width/height to the new bounds and re-clamps x/y so nothing sits off-grid.
    private reflowComponentsForBounds(pageKey: string, maxCols: number, maxRows: number): UpdateReportComponent[] {
        const state = this._store.selectSnapshot(PageState);
        const componentKeys = state.entities[pageKey]?.componentKeys || [];
        if (!componentKeys.length) return [];

        const componentEntities = this._store.selectSnapshot(ComponentState.getEntities);
        const actions: UpdateReportComponent[] = [];

        componentKeys.forEach(key => {
            const component = componentEntities[key]?.component;
            if (!component) return;

            const width = Math.max(2, Math.min(component.width, maxCols));
            const height = Math.max(2, Math.min(component.height, maxRows));
            const x = Math.max(0, Math.min(component.x, maxCols - width));
            const y = Math.max(0, Math.min(component.y, maxRows - height));

            if (width === component.width && height === component.height && x === component.x && y === component.y) return;

            actions.push(new UpdateReportComponent(key, { x, y, cols: width, rows: height }));
        });

        return actions;
    }

    @Action(ApplyReportPageOrientation)
    applyReportPageOrientation(ctx: StateContext<PageStateModel>, action: ApplyReportPageOrientation) {
        let state = ctx.getState();
        let pageSize: Partial<CultevaGridsterConfig>;

        if (action.orientation == 'portrait') pageSize = PORTRAIT_PAGE_OPTIONS;
        else pageSize = LANDSCAPE_PAGE_OPTIONS;

        // Read via the report state's token (not the AdvancedReportState class) to avoid a
        // circular import between this file and advanced-report.state.ts, which already imports
        // PageState from here.
        const reportState = this._store.selectSnapshot(REPORT_STATE_TOKEN);
        const reportModel = reportState?.entities[state.reportKey];
        const density = resolveGridDensity(reportModel?.report?.options?.config);

        let actions = [];
        let geometryWrites = [];

        action.pageKeys.forEach(key => {
            let model: PageModel = state.entities[key];

            let options: PageOptionConfig = {
                ...model.page.options?.config,
                gridOptions: {
                    ...model.page.options?.config?.gridOptions,
                    ...pageSize,
                },
                pageOrientation: action.orientation,
            };

            actions.push(new EditPageOptions(model.page.options.key, key, options, model.page.options));

            // The orientation swap changes the page's maxCols/maxRows; clamp any component that
            // would fall outside the new bounds. Unlike density's rescale (which already writes
            // every component regardless), orientation change writes NO geometry today, so only
            // components that actually need to move get a write here.
            const grid = scaleGridOptions({
                ...PAGE_OPTION_DEFAULT.gridOptions,
                ...options.gridOptions,
            }, density);

            model.componentKeys.forEach((componentKey: string) => {
                const componentModel: ComponentModel = this._store.selectSnapshot(ComponentState.getComponent(componentKey));
                const component = componentModel?.component;
                if (!component) return;

                const clamped = clampComponentGeometry(component, grid.maxCols, grid.maxRows);

                const unchanged = clamped.x === component.x && clamped.y === component.y
                    && clamped.width === component.width && clamped.height === component.height;
                if (unchanged) return;

                const payload: BaseReportComponent = JSON.parse(JSON.stringify(component));
                delete payload.options;

                payload.x = clamped.x;
                payload.y = clamped.y;
                payload.width = clamped.width;
                payload.height = clamped.height;

                geometryWrites.push(this._componentService.update(component.key, payload));
            });
        });

        const writes$ = geometryWrites.length > 0 ? forkJoin(geometryWrites) : of(null);

        // Write any clamp fixes first; only flip the page orientation once every write succeeds,
        // so a partial failure can't leave the report in a mixed/corrupt state.
        return writes$.pipe(
            switchMap(() => ctx.dispatch(actions))
        );
    }

    @Action(AddReportComponent)
    addReportComponent(ctx: StateContext<PageStateModel>, action: AddReportComponent) {

        if (!this.editModeCheck()) return;

        return this._componentService.create(action.data)
            .pipe(
                tap(
                    result => {
                        const state = ctx.getState();

                        let componentKeys = state.entities[action.pageKey].componentKeys;

                        componentKeys = [...componentKeys, result.key]

                        ctx.dispatch(new AddComponentOptions(action.pageKey, result.key, result.type, componentKeys, action.sourceOptions));
                    },
                    error => {
                        const state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(AddComponentOptions)
    addComponentOptions(ctx: StateContext<PageStateModel>, action: AddComponentOptions) {

        let state = ctx.getState();
        let options: NewComponentOption<TextComponentData | ImageComponentData | MeasureChartComponentData | TableComponentData>;

        if (!this.editModeCheck()) return;

        options = action.sourceOptions
            ? { componentKey: action.componentKey, data: { ...action.sourceOptions.data }, config: { ...action.sourceOptions.config } }
            : this.getNewReportOptions(action.componentKey, action.componentType);

        return this._componentOptionService.create(options)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    componentKeys: [...action.componentKeys],
                                }
                            }
                        });
                    },
                    error => {
                        state = ctx.getState();

                        ctx.patchState({
                            entities: {
                                ...state.entities,
                                [action.pageKey]: {
                                    ...state.entities[action.pageKey],
                                    status: translateCommonErrorStatus(error),
                                }
                            }
                        });
                    }
                )
            )
    }

    @Action(RemoveComponentFromPage)
    removeComponentFromPage(ctx: StateContext<PageStateModel>, action: RemoveComponentFromPage) {
        let state = ctx.getState();

        let page: PageModel = JSON.parse(JSON.stringify(state.entities[action.key]));

        page.componentKeys = page.componentKeys.filter(key => key !== action.componentKey);

        ctx.patchState({
            ...state,
            entities: {
                ...state.entities,
                [action.key]: {
                    ...page,
                }
            }
        })
    }

    private getNewReportOptions(componentKey: string, type: ReportComponentType): NewComponentOption<TextComponentData | ImageComponentData | MeasureChartComponentData | TableComponentData> {
        let defaults = null;

        if (type === ReportComponentType.TEXT) defaults = TEXT_OPTION_DEFAULT;
        if (type === ReportComponentType.IMAGE) defaults = IMAGE_OPTION_DEFAULT;
        if (type === ReportComponentType.CHART) defaults = CHART_OPTION_DEFAULT;
        if (type === ReportComponentType.INDEX) defaults = INDEX_OPTION_DEFAULT;
        if (type === ReportComponentType.TABLE) defaults = TABLE_OPTION_DEFAULT;

        return {
            componentKey: componentKey,
            data: {
                ...defaults
            },
            config: {
                ...COMPONENT_CONFIG_DEFAULT,
            }
        };
    }

    private editModeCheck() {
        const reportState = this._store.selectSnapshot(REPORT_STATE_TOKEN);

        if (!reportState.editMode) {
            console.warn('[PageState] Action Blocked While In Preview Mode');
            return false
        }

        return true
    }
}